import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal, Message } from 'semantic-ui-react';
import { defineMessages, useIntl } from 'react-intl';
import imageBlockSVG from '@plone/volto/components/manage/Blocks/Image/block-image.svg';
import { Icon } from '@plone/volto/components';
import clearSVG from '@plone/volto/icons/clear.svg';
import { softHyphenSupport, PreviewImg } from 'volto-dlr/helpers';
import { flattenToAppURL, BodyClass } from '@plone/volto/helpers';
import { getContent } from '@plone/volto/actions';
import { find, map } from 'lodash';
import galleryRightSVG from 'volto-dlr/icons/rightnav.svg';
import galleryLeftSVG from 'volto-dlr/icons/leftnav.svg';

import IconChart from './Charts/Icon';
import PieChart from './Charts/Pie';
import BarChart from './Charts/Bar';
import LineChart from './Charts/Line';
import GroupedBarChart from './Charts/GroupedBar';

const colors = [
  '#00658B',
  '#D2AE3D',
  '#82A043',
  '#6CB9DC',
  '#F8DE53',
  '#CAD55C',
  '#D1E8FA',
  '#E6EAAF',
  '#FFF8BE',
  '#EBEBEB',
];

const messages = defineMessages({
  PleaseChooseContent: {
    id: 'Please choose an existing content as source for this element',
    defaultMessage:
      'Please choose an existing content as source for this element',
  },
});

const getTeasers = (blocks) => {
  const teasers = [];

  map(blocks, (block) => {
    if (block['@type'] === 'teaser' && block.href?.[0]?.['@type'] === 'Chart') {
      teasers.push({
        id: block.id,
        href: block.href[0]['@id'],
        title: block.title || '',
      });
    }
    if (block['@type'] === '__grid') {
      map(block.columns, (column) => {
        if (
          column['@type'] === 'teaser' &&
          column.href?.[0]?.['@type'] === 'Chart'
        ) {
          teasers.push({
            id: column.id,
            href: column.href[0]['@id'],
            title: column.title || '',
          });
        }
      });
    }
  });
  return teasers;
};

const getPrev = (teasers, curBlock) => {
  const index = teasers.findIndex((item) => item.id === curBlock);
  if (index > 0) {
    return teasers[index - 1];
  }
  return null;
};

const getNext = (teasers, curBlock) => {
  const index = teasers.findIndex((item) => item.id === curBlock);
  if (index < teasers.length - 1) {
    return teasers[index + 1];
  }
  return null;
};

const ChartItemTemplate = (props) => {
  const { data, isEditMode, className, insideGridBlock = false, block } = props;
  const intl = useIntl();
  const href = data.href?.[0];
  const image = data.preview_image?.[0];
  const [open, setOpen] = useState(false);
  const [curBlock, setCurBlock] = useState(block);
  const [curData, setCurData] = useState({});
  const dispatch = useDispatch();
  const blocks_layout = useSelector(
    (state) =>
      state.form?.global?.blocks_layout?.items ||
      state.content?.data?.blocks_layout?.items ||
      [],
  );
  const locale = useSelector((state) => state.intl?.locale || 'en');
  const blocks_data = useSelector(
    (state) => state.form?.global?.blocks || state.content?.data?.blocks || {},
  );
  const teasers = getTeasers(
    map(blocks_layout, (id) => ({ id, ...blocks_data[id] })),
  );
  const prev = getPrev(teasers, curBlock);
  const next = getNext(teasers, curBlock);

  useEffect(() => {
    dispatch(
      getContent(
        flattenToAppURL(find(teasers, { id: curBlock }).href),
        null,
        'chart',
      ),
    ).then((resp) => setCurData(resp));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, curBlock, JSON.stringify(teasers)]);

  const title = curData.title || '';
  const subtitle = curData.subtitle || '';
  const description = curData.description || '';
  const json = curData.json ? JSON.parse(curData.json) : null;
  const type = curData.chart_type?.token || 'Pie Chart';
  const icon_link = `${curData.icon_link?.['@id'] || ''}/${
    curData.icon_link?.image_scales?.image?.[0]?.download || ''
  }`;
  const icon_title = curData.icon_link?.title;

  let rows = [];

  if (type === 'Grouped Bar Chart' && json) {
    rows = json.map((item, index) => {
      return Object.fromEntries(
        item.map((subitem, subindex) => [
          subindex === 0 ? 'name' : `v${subindex - 1}`,
          subitem,
        ]),
      );
    });
    rows.unshift(
      json[0].map((subitem, subindex) =>
        subindex === 0 ? null : colors[(subindex - 1) % colors.length],
      ),
    );
  }

  if (type !== 'Grouped Bar Chart' && json) {
    rows = json.map((item, index) => {
      return {
        name: item[0],
        v0: item[1],
        color: colors[index % colors.length],
      };
    });
  }

  return (
    <div
      className={cx('block teaser', className, {
        standalone: !insideGridBlock,
      })}
    >
      {!href && isEditMode && (
        <Message>
          <div className="grid-teaser-item placeholder">
            <img src={imageBlockSVG} alt="" />
            <p>{intl.formatMessage(messages.PleaseChooseContent)}</p>
          </div>
        </Message>
      )}
      {href && (
        <div
          className={`grid-teaser-item default chartItem ${
            data.hideImage ? 'hideImage' : ''
          }`}
          onClick={() => !isEditMode && setOpen(true)}
          onKeyDown={() => !isEditMode && setOpen(true)}
          role="button"
          tabIndex="0"
        >
          {(href.hasPreviewImage || href.image_field || image) &&
            !data.hideImage && (
              <div className="grid-image-wrapper">
                <PreviewImg
                  previewImage={image}
                  href={href}
                  scaleName="teaser"
                  targetWidth={
                    !insideGridBlock && data.styles?.align === 'center'
                      ? 'default'
                      : 'half'
                  }
                />
              </div>
            )}
          <div className="content">
            <h2>{softHyphenSupport(data?.title)}</h2>
            <p>{data?.description ?? ''}</p>
          </div>
        </div>
      )}
      <Modal
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        open={open}
        size="large"
        className="chart-modal"
        dimmer="chart"
      >
        <BodyClass className="has-chart-modal" />
        <div className="chart-modal-inner">
          <Button
            basic
            className="close"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          >
            <Icon name={clearSVG} size="30px" />
          </Button>
          <h2>{title}</h2>
          {subtitle && <h3>{subtitle}</h3>}
          <p className="description">{description ? description : ''}&nbsp;</p>
          <div className="chart-wrapper">
            {type === 'Pie Chart' && (
              <PieChart
                data={rows}
                icon_link={icon_link}
                icon_title={icon_title}
                locale={locale}
              />
            )}
            {type === 'Line Chart' && <LineChart data={rows} locale={locale} />}
            {type === 'Bar Chart' && <BarChart data={rows} locale={locale} />}
            {type === 'Icon' && (
              <IconChart
                data={rows}
                icon_link={icon_link}
                icon_title={icon_title}
                locale={locale}
              />
            )}
            {type === 'Grouped Bar Chart' && (
              <GroupedBarChart data={rows} locale={locale} />
            )}
          </div>
        </div>
        {teasers.length > 1 && (
          <div className="chart-prev-next">
            <div className="chart-prev-next-inner">
              {prev && (
                <Button
                  basic
                  onClick={() => setCurBlock(prev.id)}
                  className="prev"
                >
                  <Icon name={galleryLeftSVG} size="50px" color="#ffffff00" />
                  <div className="text">{prev.title}</div>
                </Button>
              )}
              {next && (
                <Button
                  basic
                  onClick={() => setCurBlock(next.id)}
                  className="next"
                >
                  <div className="text">{next.title}</div>
                  <Icon name={galleryRightSVG} size="50px" color="#ffffff00" />
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

ChartItemTemplate.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  isEditMode: PropTypes.bool,
};

export default ChartItemTemplate;
